import React, { useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../context/AuthContext';
import { 
  Card, 
  CardHeader, 
  CardBody, 
  Button, 
  Input,
  Label,
  FormGroup,
  TextArea
} from '../../styles/StyledComponents';
import IconWrapper from '../common/IconWrapper';
import { 
  FaUser, 
  FaEdit, 
  FaSave, 
  FaTimes, 
  FaEnvelope, 
  FaPhone, 
  FaMapMarkerAlt,
  FaBuilding,
  FaClock,
  FaHistory,
  FaCheckCircle,
  FaEye,
  FaMoneyBillWave
} from 'react-icons/fa';

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const ProfileHeader = styled.div`
  margin-bottom: 2rem;
  
  h1 {
    color: var(--secondary-color);
    margin-bottom: 0.5rem;
    font-size: 1.8rem;
    
    @media (max-width: 768px) {
      font-size: 1.5rem;
    }
    
    @media (max-width: 480px) {
      font-size: 1.3rem;
    }
  }
  
  p {
    color: var(--dark-gray);
    font-size: 1.1rem;
    
    @media (max-width: 768px) {
      font-size: 1rem;
    }
  }
`;

const ProfileGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const ProfileCard = styled(Card)`
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  
  .profile-header {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1.5rem;
    
    @media (max-width: 768px) {
      flex-direction: column;
      text-align: center;
    }
    
    .profile-avatar {
      width: 80px;
      height: 80px;
      border-radius: 50%;
      background: linear-gradient(135deg, var(--primary-color), #e07a10);
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-size: 2rem;
      font-weight: 700;
      
      @media (max-width: 768px) {
        width: 70px;
        height: 70px;
        font-size: 1.8rem;
      }
    }
    
    .profile-info {
      flex: 1;
      
      .name {
        font-size: 1.5rem;
        font-weight: 700;
        color: var(--secondary-color);
        margin-bottom: 0.25rem;
        
        @media (max-width: 768px) {
          font-size: 1.3rem;
        }
      }
      
      .role {
        color: var(--primary-color);
        font-weight: 600;
        margin-bottom: 0.5rem;
        font-size: 1rem;
        
        @media (max-width: 768px) {
          font-size: 0.9rem;
        }
      }
      
      .status {
        display: inline-block;
        padding: 0.25rem 0.75rem;
        background: var(--success-color);
        color: white;
        border-radius: 12px;
        font-size: 0.8rem;
        font-weight: 600;
        
        @media (max-width: 768px) {
          font-size: 0.75rem;
        }
      }
    }
  }
  
  .contact-info {
    .contact-item {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      padding: 0.75rem 0;
      border-bottom: 1px solid var(--light-gray);
      
      &:last-child {
        border-bottom: none;
      }
      
      @media (max-width: 768px) {
        flex-direction: column;
        align-items: flex-start;
        gap: 0.25rem;
      }
      
      .icon {
        color: var(--primary-color);
        width: 20px;
        flex-shrink: 0;
      }
      
      .label {
        font-weight: 500;
        color: var(--dark-gray);
        min-width: 80px;
        font-size: 0.9rem;
        
        @media (max-width: 768px) {
          font-size: 0.85rem;
          min-width: auto;
        }
      }
      
      .value {
        color: var(--secondary-color);
        font-weight: 500;
        font-size: 0.95rem;
        
        @media (max-width: 768px) {
          font-size: 0.9rem;
        }
      }
    }
  }
`;

const ActivityCard = styled(Card)`
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  
  .activity-header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 1rem;
    
    .icon {
      color: var(--primary-color);
      font-size: 1.2rem;
    }
    
    h3 {
      color: var(--secondary-color);
      font-size: 1.2rem;
      
      @media (max-width: 768px) {
        font-size: 1.1rem;
      }
    }
  }
  
  .activity-item {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    padding: 1rem 0;
    border-bottom: 1px solid var(--light-gray);
    
    &:last-child {
      border-bottom: none;
    }
    
    @media (max-width: 768px) {
      gap: 0.75rem;
    }
    
    .activity-icon {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      background: var(--light-gray);
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--primary-color);
      flex-shrink: 0;
      
      @media (max-width: 768px) {
        width: 35px;
        height: 35px;
      }
    }
    
    .activity-content {
      flex: 1;
      
      .activity-title {
        font-weight: 600;
        color: var(--secondary-color);
        margin-bottom: 0.25rem;
        font-size: 1rem;
        
        @media (max-width: 768px) {
          font-size: 0.9rem;
        }
      }
      
      .activity-description {
        color: var(--dark-gray);
        font-size: 0.9rem;
        margin-bottom: 0.5rem;
        line-height: 1.4;
        
        @media (max-width: 768px) {
          font-size: 0.85rem;
        }
      }
      
      .activity-time {
        font-size: 0.8rem;
        color: var(--medium-gray);
        
        @media (max-width: 768px) {
          font-size: 0.75rem;
        }
      }
    }
  }
`;

const EditForm = styled.form`
  .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    margin-bottom: 1rem;
    
    @media (max-width: 768px) {
      grid-template-columns: 1fr;
      gap: 0.75rem;
    }
  }
  
  .form-actions {
    display: flex;
    gap: 1rem;
    margin-top: 1.5rem;
    
    @media (max-width: 768px) {
      flex-direction: column;
      gap: 0.75rem;
    }
  }
`;

const NoDataMessage = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: var(--dark-gray);
  
  .empty-icon {
    font-size: 4rem;
    color: var(--light-gray);
    margin-bottom: 1rem;
    
    @media (max-width: 768px) {
      font-size: 3rem;
    }
  }
  
  .empty-title {
    font-size: 1.3rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
    
    @media (max-width: 768px) {
      font-size: 1.1rem;
    }
  }
  
  .empty-description {
    font-size: 1rem;
    line-height: 1.6;
    max-width: 400px;
    margin: 0 auto;
    
    @media (max-width: 768px) {
      font-size: 0.9rem;
      max-width: 300px;
    }
  }
`;

const SupervisorProfile: React.FC = () => {
  const { user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: '+255 123 456 789',
    address: 'Dar es Salaam, Tanzania',
    department: 'Supervision',
    bio: 'Experienced supervisor with expertise in project management and team coordination.'
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    // In a real app, this would make an API call
    console.log('Saving profile:', formData);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setFormData({
      name: user?.name || '',
      email: user?.email || '',
      phone: '+255 123 456 789',
      address: 'Dar es Salaam, Tanzania',
      department: 'Supervision',
      bio: 'Experienced supervisor with expertise in project management and team coordination.'
    });
    setIsEditing(false);
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Mock activity data
  const recentActivities = [
    {
      id: 1,
      type: 'approval',
      title: 'Approved Fund Request',
      description: 'Approved fund request FR-2024-001 for project materials',
      timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000) // 2 hours ago
    },
    {
      id: 2,
      type: 'review',
      title: 'Reviewed Project Progress',
      description: 'Reviewed progress report for assigned project',
      timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000) // 1 day ago
    },
    {
      id: 3,
      type: 'request',
      title: 'Submitted Fund Request',
      description: 'Created new fund request for equipment purchase',
      timestamp: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000) // 3 days ago
    }
  ];

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'approval':
        return FaCheckCircle;
      case 'review':
        return FaEye;
      case 'request':
        return FaMoneyBillWave;
      default:
        return FaHistory;
    }
  };

  if (!user) {
    return (
      <PageContainer>
        <NoDataMessage>
          <div className="empty-icon">👤</div>
          <div className="empty-title">User Not Found</div>
          <div className="empty-description">
            Unable to load user profile. Please try again later.
          </div>
        </NoDataMessage>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <ProfileHeader>
        <h1>My Profile</h1>
        <p>Manage your personal information and view recent activities</p>
      </ProfileHeader>

      <ProfileGrid>
        <ProfileCard>
          <div className="profile-header">
            <div className="profile-avatar">
              {user.name.charAt(0).toUpperCase()}
            </div>
            <div className="profile-info">
              <div className="name">{user.name}</div>
              <div className="role">Supervisor</div>
              <div className="status">Active</div>
            </div>
          </div>

          <div className="contact-info">
            <div className="contact-item">
              <IconWrapper icon={FaEnvelope} className="icon" />
              <span className="label">Email:</span>
              <span className="value">{user.email}</span>
            </div>
            <div className="contact-item">
              <IconWrapper icon={FaPhone} className="icon" />
              <span className="label">Phone:</span>
              <span className="value">{formData.phone}</span>
            </div>
            <div className="contact-item">
              <IconWrapper icon={FaMapMarkerAlt} className="icon" />
              <span className="label">Address:</span>
              <span className="value">{formData.address}</span>
            </div>
            <div className="contact-item">
              <IconWrapper icon={FaBuilding} className="icon" />
              <span className="label">Department:</span>
              <span className="value">{formData.department}</span>
            </div>
          </div>

          {!isEditing ? (
            <div style={{ marginTop: '1.5rem' }}>
              <Button onClick={() => setIsEditing(true)}>
                <IconWrapper icon={FaEdit} />
                Edit Profile
              </Button>
            </div>
          ) : (
            <EditForm onSubmit={handleSave}>
              <FormGroup>
                <Label>Full Name</Label>
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>

              <div className="form-row">
                <FormGroup>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Phone</Label>
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                  />
                </FormGroup>
              </div>

              <div className="form-row">
                <FormGroup>
                  <Label>Address</Label>
                  <Input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Department</Label>
                  <Input
                    type="text"
                    name="department"
                    value={formData.department}
                    onChange={handleInputChange}
                  />
                </FormGroup>
              </div>

              <FormGroup>
                <Label>Bio</Label>
                <TextArea
                  name="bio"
                  value={formData.bio}
                  onChange={handleInputChange}
                  rows={4}
                />
              </FormGroup>

              <div className="form-actions">
                <Button type="submit" style={{ backgroundColor: 'var(--success-color)' }}>
                  <IconWrapper icon={FaSave} />
                  Save Changes
                </Button>
                <Button type="button" onClick={handleCancel} style={{ backgroundColor: 'var(--danger-color)' }}>
                  <IconWrapper icon={FaTimes} />
                  Cancel
                </Button>
              </div>
            </EditForm>
          )}
        </ProfileCard>

        <ActivityCard>
          <div className="activity-header">
            <IconWrapper icon={FaHistory} className="icon" />
            <h3>Recent Activities</h3>
          </div>

          {recentActivities.length > 0 ? (
            recentActivities.map((activity) => (
              <div key={activity.id} className="activity-item">
                <div className="activity-icon">
                  <IconWrapper icon={getActivityIcon(activity.type)} />
                </div>
                <div className="activity-content">
                  <div className="activity-title">{activity.title}</div>
                  <div className="activity-description">{activity.description}</div>
                  <div className="activity-time">{formatDate(activity.timestamp)}</div>
                </div>
              </div>
            ))
          ) : (
            <NoDataMessage>
              <div className="empty-icon">📋</div>
              <div className="empty-title">No Recent Activities</div>
              <div className="empty-description">
                Your recent activities will appear here.
              </div>
            </NoDataMessage>
          )}
        </ActivityCard>
      </ProfileGrid>
    </PageContainer>
  );
};

export default SupervisorProfile; 